import {useCallback} from 'react';
import {
	useAccountInformation,
	useHandleNewAppointment,
	useMediaQueries,
	useQueryParams,
} from 'presentation/pages/private/hooks';
import {useGetHealthAppointmentsHistoryQuery} from 'presentation/modules/myAppointments/queries/useGetHealthAppointmentsHistoryQuery';
import {filterAppointmentsHistory} from '../../utils/filterAppointmentsHistory';
import {AppointmentHistoryContentType} from 'domain/usecases';
import {TypeAttendance} from 'presentation/modules/myAppointments/constants/typeAttendanceEnum';
import {MyAppointmentsEnum} from 'presentation/constants';
import {useContractStatus} from 'presentation/hooks';
import {getAppointmentsEmptyDescription} from 'presentation/modules/myAppointments/utils/getAppointmentsEmptyDescription';
import {getAppointmentHistoryBookingType} from 'presentation/utils';
import {HistoryFilterEnum} from 'presentation/modules/myAppointments/constants/historyFilterEnum';
import {useNavigate} from 'react-router-dom';
import {useLazyScroll} from 'presentation/hooks/useLazyScroll';
import {useMyAppointmentsList} from '../../../MyAppointmentsList/hooks/useMyAppointmentsList';
import {BookingTypeEnum} from 'presentation/modules/booking/shared/constants/bookingTypeEnum';
import {useCongenereEligibilityStore} from 'presentation/pages/private/store/useCongenereEligibilityStore';

export const useMyAppointmentsListHistory = () => {
	const {SEARCH_PARAMS} = useQueryParams();
	const {isTablet, isMobile} = useMediaQueries();
	const navigate = useNavigate();
	const {hideHealthHospitalComponent, odontoOnly} = useAccountInformation();
	const {handleNewAppointment} = useHandleNewAppointment();
	const {isValid, isContractSuspended} = useContractStatus();
	const {bookingType} = SEARCH_PARAMS;
	const {
		data: appointmentsListHistory,
		isError: appointmentsListHistoryError,
		isLoading: appointmentsListHistoryLoading,
		refetch,
	} = useGetHealthAppointmentsHistoryQuery('always');
	const {list} = useMyAppointmentsList();
	const {congenereEligibility} = useCongenereEligibilityStore();

	const hasHealthBookings =
		list.filter(item => item.bookingType === BookingTypeEnum.HEALTH).length > 0;

	const listHasCardWithFavoriteButton = list.some(
		item =>
			item.bookingType === BookingTypeEnum.HEALTH ||
			item.bookingType === BookingTypeEnum.TELEHEALTH,
	);

	const filteredAppointmentsListHistory = useCallback(() => {
		if (!appointmentsListHistory) return [];
		return filterAppointmentsHistory({
			filter: bookingType,
			appointmentsList: appointmentsListHistory || [],
		});
	}, [bookingType, appointmentsListHistory]);
	const appointmentsListHistoryFiltered = filteredAppointmentsListHistory();

	const appointmentsListHistoryHasValue = !!appointmentsListHistory;
	const appointmentsListHistoryFilteredEmpty = appointmentsListHistoryFiltered.length === 0;

	const {items, scrollableContentRef, loadingNewPageState, handleVisible} =
		useLazyScroll<AppointmentHistoryContentType>({
			itemsList: appointmentsListHistoryHasValue ? appointmentsListHistoryFiltered : [],
			itemsPerPage: isTablet ? 3 : 8,
			loadingTime: 600,
		});

	const shouldHideScheduleButton = Boolean(
		hideHealthHospitalComponent || !isValid || isContractSuspended || congenereEligibility,
	);

	const emptyStateDescription = getAppointmentsEmptyDescription({
		isValid,
		isContractSuspended,
		odontoOnly,
	});

	const hasTelemedicineOrEmergency = items.some(
		item =>
			getAppointmentHistoryBookingType({
				telemedicine: item.clinic?.telemedicine,
				typeAttendance: item.typeAttendance,
			}) !== HistoryFilterEnum.HEALTH,
	);

	const firstNonHealthIndex = items?.findIndex(item => {
		const bookingType = getAppointmentHistoryBookingType({
			telemedicine: item.clinic?.telemedicine,
			typeAttendance: item.typeAttendance,
		});
		return bookingType !== HistoryFilterEnum.HEALTH;
	});

	const handleGoToDetails = ({
		typeAttendance,
		appointmentIdPublic,
		number,
	}: AppointmentHistoryContentType) => {
		const id = typeAttendance === TypeAttendance.EMERGENCY ? appointmentIdPublic : number;
		const isSurgery = typeAttendance === TypeAttendance.SURGERY;
		const path = isSurgery
			? MyAppointmentsEnum.MY_APPOINTMENTS_HISTORY_SURGERY_DETAILS
			: MyAppointmentsEnum.MY_APPOINTMENTS_HISTORY_DETAILS;

		navigate(path.replace(':id', String(id)), {
			state: typeAttendance,
		});
	};

	return {
		appointmentsListHistory,
		appointmentsListHistoryFiltered,
		refetch,
		appointmentsListHistoryHasValue,
		appointmentsListHistoryError,
		appointmentsListHistoryLoading,
		appointmentsListHistoryFilteredEmpty,
		firstNonHealthIndex,
		hasTelemedicineOrEmergency,
		handleGoToDetails,
		scrollableContentRef,
		handleVisible,
		loadingNewPageState,
		isTablet,
		isMobile,
		shouldHideScheduleButton,
		emptyStateDescription,
		handleNewAppointment,
		list: items,
		hasHealthBookings,
		listHasCardWithFavoriteButton,
	};
};
